import React from 'react';
import { SectionType } from 'types/publishedAssets';
import classNames from 'classnames';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { Spacer } from 'components/Atoms/Spacer/Spacer';
import { determineSectionId } from 'utils/determineSectionId';
import { isHeroSection } from 'utils/heroSection';
import styles from './Section.module.scss';
import { Content } from './Content';
import { TitleText } from './Atoms/TitleText';
import { Hero } from './ContentTypes/Hero/Hero';
import { SectionTwoColumn } from './SectionTwoColumn';
import { useSectionScrollMarginTop } from './useSectionScrollMarginTop';

interface SectionProps {
  section: SectionType;
  index?: number;
  sections: SectionType[];
  navigationMode?: 'anchor' | 'tabs';
}

export function Section(props: SectionProps) {
  const { section, index, sections, navigationMode } = props;
  const { translate, translateDefault } = useGetContentTranslations();
  const isHero = isHeroSection(section);
  const label = translate(section.translations, 'label');
  const sectionId = determineSectionId(translateDefault(section.translations, 'label'));
  const scrollMarginTop = useSectionScrollMarginTop({ enabled: navigationMode === 'anchor' && !isHero });

  if (isHero) {
    return <Hero content={section.section_entities[0]} />;
  }
  const previousSection = sections?.[(index || 1) - 1];
  const isAfterHeroSection = !!previousSection && isHeroSection(previousSection);

  // The tab bar already shows this section's title as the active tab label in tabs mode,
  // but the heading stays in the DOM (visually hidden) so the section keeps an accessible
  // name and a place in the heading outline for screen-reader/keyboard users.
  const hasTitle = !!label;
  const isTabsMode = navigationMode === 'tabs';
  const headingId = hasTitle && sectionId ? `${sectionId}-heading` : undefined;

  if (section.metadata?.numberOfColumns === 2) {
    return (
      <div
        id={sectionId || undefined}
        style={scrollMarginTop}
        role={headingId ? 'region' : undefined}
        aria-labelledby={headingId}
      >
        <SectionTwoColumn
          section={section}
          isAfterHeroSection={isAfterHeroSection}
          title={label || undefined}
          visuallyHideTitle={isTabsMode}
          headingId={headingId}
        />
      </div>
    );
  }

  return (
    <div
      id={sectionId || undefined}
      className={classNames({ [styles.singleContentSection]: !isHero })}
      style={scrollMarginTop}
      role={headingId ? 'region' : undefined}
      aria-labelledby={headingId}
    >
      {hasTitle && (
        <>
          {/** skip hr if this section is after the hero section, or the title is only for a11y */}
          {!isAfterHeroSection && !isTabsMode && <hr className={styles.sectionTitleSeparator} />}
          <TitleText text={label} id={headingId} type="sectionTitle" visuallyHidden={isTabsMode} />
          <Spacer orientation="vertical" size="small" />
        </>
      )}
      {section.section_entities.map((entity) => (
        <React.Fragment key={entity.id}>
          <Content key={entity.id} content={entity} section={section} />
          <Spacer orientation="vertical" size="medium" />
        </React.Fragment>
      ))}
    </div>
  );
}
